// ─────────────────────────────────────────────────────────────────────────────
// Fish drawn in code, in the same ink-outlined style as the rest of the ocean.
// Each species (lib/aquarium.ts) is a body shape, colours and a pattern; it's
// drawn once into a canvas, facing LEFT (head on the left, tail on the right,
// like the hand-drawn art was), and animated with lib/fishIdle.ts like any
// picture. speciesImage() turns the same drawing into an <img> src.
//
// (Josiah's hand-drawn fish are parked in public/aquarium/ for later.)
// ─────────────────────────────────────────────────────────────────────────────

export type Shape = 'oval' | 'tall' | 'long' | 'round' | 'ray' | 'seahorse' | 'jelly' | 'crab' | 'star' | 'squid' | 'lantern' | 'dragon' | 'otter';
export type Pattern = 'none' | 'stripes' | 'bars' | 'spots' | 'band' | 'swirl' | 'rainbow' | 'scales';

export type FishLook = {
  shape: Shape;
  /** Body, belly / second colour, fins, pattern. */
  body: string; belly: string; fin: string; mark: string;
  pattern: Pattern;
  /** Glowing bits (deep-sea and magic fish); the ocean lights the water around them too. */
  glow?: string;
  /** A shimmer of sparkles (epic and legendary fish). */
  shiny?: boolean;
};

const INK = '#1e2633';
const S = 320;   // the drawing's canvas; the fish is cropped out of it afterwards

function eye(g: CanvasRenderingContext2D, x: number, y: number, r: number) {
  g.fillStyle = '#ffffff'; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); g.stroke();
  g.fillStyle = INK; g.beginPath(); g.arc(x - r * 0.25, y, r * 0.55, 0, Math.PI * 2); g.fill();
  g.fillStyle = '#ffffff'; g.beginPath(); g.arc(x - r * 0.45, y - r * 0.3, r * 0.2, 0, Math.PI * 2); g.fill();
}

/** Fill the current path with the body colour and pattern, then outline it. */
function paintBody(g: CanvasRenderingContext2D, path: Path2D, f: FishLook, box: [number, number, number, number]) {
  const [x0, y0, x1, y1] = box;
  if (f.pattern === 'rainbow') {
    const lg = g.createLinearGradient(x0, 0, x1, 0);
    ['#ff5f6d', '#ffc371', '#fff36b', '#5dfc8d', '#4fc3ff', '#b57bff'].forEach((c, i, a) => lg.addColorStop(i / (a.length - 1), c));
    g.fillStyle = lg;
  } else {
    const lg = g.createLinearGradient(0, y0, 0, y1);
    lg.addColorStop(0, f.body); lg.addColorStop(0.62, f.body); lg.addColorStop(1, f.belly);
    g.fillStyle = lg;
  }
  g.fill(path);
  g.save();
  g.clip(path);
  g.fillStyle = f.mark; g.strokeStyle = f.mark;
  const w = x1 - x0, h = y1 - y0;
  switch (f.pattern) {
    case 'stripes':   // thin lines along the body
      g.lineWidth = h * 0.07;
      for (let k = 1; k <= 3; k++) { const y = y0 + (h * k) / 4.2; g.beginPath(); g.moveTo(x0, y); g.quadraticCurveTo((x0 + x1) / 2, y - h * 0.06, x1, y); g.stroke(); }
      break;
    case 'bars':      // bold bands across, outlined in ink (clownfish, angelfish)
      for (const k of [0.3, 0.55, 0.8]) {
        const x = x0 + w * k;
        g.fillStyle = f.mark; g.fillRect(x - w * 0.06, y0, w * 0.12, h);
        g.strokeStyle = INK; g.lineWidth = 3; g.strokeRect(x - w * 0.06, y0 - 10, w * 0.12, h + 20);
      }
      break;
    case 'spots':
      for (let k = 0; k < 14; k++) {
        const x = x0 + w * (0.15 + ((k * 0.618) % 1) * 0.75), y = y0 + h * (0.2 + ((k * 0.382 + 0.1) % 1) * 0.6);
        g.beginPath(); g.arc(x, y, Math.min(w, h) * (0.04 + (k % 3) * 0.015), 0, Math.PI * 2); g.fill();
      }
      break;
    case 'band':      // one broad stripe along the middle
      g.fillRect(x0, y0 + h * 0.4, w, h * 0.2);
      break;
    case 'swirl':     // mandarin-style wavy lines
      g.lineWidth = h * 0.06;
      for (let k = 0; k < 5; k++) {
        g.beginPath();
        for (let x = x0; x <= x1; x += 6) { const y = y0 + h * (0.15 + k * 0.18) + Math.sin(x / 14 + k) * h * 0.06; if (x === x0) g.moveTo(x, y); else g.lineTo(x, y); }
        g.stroke();
      }
      break;
    case 'scales':
      g.lineWidth = 2; g.strokeStyle = f.mark;
      for (let y = y0; y < y1; y += 16) for (let x = x0 + ((y / 16) % 2) * 9; x < x1; x += 18) { g.beginPath(); g.arc(x, y, 9, 0, Math.PI); g.stroke(); }
      break;
  }
  g.restore();
  g.strokeStyle = INK; g.lineWidth = 5;
  g.stroke(path);
}

function tailFork(g: CanvasRenderingContext2D, x: number, y: number, len: number, spread: number, f: FishLook) {
  const p = new Path2D();
  p.moveTo(x - 6, y);
  p.quadraticCurveTo(x + len * 0.5, y - spread * 0.3, x + len, y - spread);
  p.quadraticCurveTo(x + len * 0.7, y, x + len, y + spread);
  p.quadraticCurveTo(x + len * 0.5, y + spread * 0.3, x - 6, y);
  g.fillStyle = f.fin; g.fill(p); g.strokeStyle = INK; g.lineWidth = 5; g.stroke(p);
}

function fin(g: CanvasRenderingContext2D, pts: [number, number][], f: FishLook) {
  const p = new Path2D();
  p.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length - 1; i += 2) p.quadraticCurveTo(pts[i][0], pts[i][1], pts[i + 1][0], pts[i + 1][1]);
  p.closePath();
  g.fillStyle = f.fin; g.fill(p); g.strokeStyle = INK; g.lineWidth = 5; g.stroke(p);
}

function sparkle(g: CanvasRenderingContext2D, x: number, y: number, r: number) {
  g.save(); g.fillStyle = '#ffffff'; g.strokeStyle = INK; g.lineWidth = 1.5;
  g.beginPath();
  for (let i = 0; i < 8; i++) { const a = (i * Math.PI) / 4, rr = i % 2 ? r * 0.35 : r; g.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); }
  g.closePath(); g.fill(); g.stroke(); g.restore();
}

/** Draw a fish centred at (0, 0) in a S×S canvas, facing left. */
function drawFish(g: CanvasRenderingContext2D, f: FishLook) {
  g.lineJoin = 'round'; g.lineCap = 'round'; g.strokeStyle = INK; g.lineWidth = 5;
  switch (f.shape) {
    case 'oval': {
      fin(g, [[-30, -48], [0, -92], [50, -40]], f);                 // dorsal
      fin(g, [[0, 44], [20, 80], [45, 38]], f);                     // anal
      tailFork(g, 84, 0, 56, 46, f);
      const p = new Path2D(); p.ellipse(0, 0, 96, 54, 0, 0, Math.PI * 2);
      paintBody(g, p, f, [-96, -54, 96, 54]);
      fin(g, [[-10, 8], [10, 34], [26, 10]], f);                    // pectoral
      eye(g, -56, -12, 13);
      g.beginPath(); g.moveTo(-94, 10); g.quadraticCurveTo(-84, 16, -76, 12); g.stroke();
      break;
    }
    case 'tall': {
      fin(g, [[-20, -60], [30, -140], [70, -40]], f);
      fin(g, [[-20, 60], [30, 140], [70, 40]], f);
      tailFork(g, 62, 0, 40, 34, f);
      const p = new Path2D(); p.ellipse(0, 0, 72, 78, 0, 0, Math.PI * 2);
      paintBody(g, p, f, [-72, -78, 72, 78]);
      eye(g, -38, -20, 12);
      g.beginPath(); g.moveTo(-70, 8); g.lineTo(-58, 12); g.stroke();
      break;
    }
    case 'long': {
      fin(g, [[0, -24], [30, -56], [70, -20]], f);
      tailFork(g, 128, 0, 34, 30, f);
      const p = new Path2D();
      p.moveTo(-150, 2); p.quadraticCurveTo(-60, -40, 60, -28); p.quadraticCurveTo(140, -16, 132, 0);
      p.quadraticCurveTo(140, 16, 60, 26); p.quadraticCurveTo(-60, 36, -150, 2); p.closePath();
      paintBody(g, p, f, [-150, -34, 136, 30]);
      eye(g, -104, -6, 9);
      break;
    }
    case 'round': {
      fin(g, [[-8, -58], [14, -86], [36, -54]], f);
      tailFork(g, 64, 0, 30, 26, f);
      g.fillStyle = f.fin;
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2;
        g.beginPath(); g.moveTo(Math.cos(a - 0.12) * 66, Math.sin(a - 0.12) * 66); g.lineTo(Math.cos(a) * 84, Math.sin(a) * 84); g.lineTo(Math.cos(a + 0.12) * 66, Math.sin(a + 0.12) * 66); g.fill(); g.stroke();
      }
      const p = new Path2D(); p.arc(0, 0, 70, 0, Math.PI * 2);
      paintBody(g, p, f, [-70, -70, 70, 70]);
      eye(g, -34, -16, 16);
      g.beginPath(); g.ellipse(-64, 14, 7, 5, 0, 0, Math.PI * 2); g.fillStyle = INK; g.fill();
      break;
    }
    case 'ray': {
      // A ray gliding, seen a little from above: a wide diamond and a whip of a tail.
      g.lineWidth = 5;
      g.beginPath(); g.moveTo(70, 0); g.quadraticCurveTo(130, 6, 160, -6); g.stroke();
      const p = new Path2D();
      p.moveTo(-110, 0); p.quadraticCurveTo(-40, -70, 10, -66); p.quadraticCurveTo(50, -40, 80, 0);
      p.quadraticCurveTo(50, 40, 10, 66); p.quadraticCurveTo(-40, 70, -110, 0); p.closePath();
      paintBody(g, p, f, [-110, -70, 80, 70]);
      eye(g, -64, -12, 8); eye(g, -64, 12, 8);
      break;
    }
    case 'seahorse': {
      // Upright: head at the top facing left, curled tail at the bottom.
      g.lineWidth = 14; g.strokeStyle = INK;
      g.beginPath(); g.moveTo(10, 40); g.quadraticCurveTo(30, 110, -10, 120); g.quadraticCurveTo(-36, 112, -18, 92); g.stroke();
      g.lineWidth = 8; g.strokeStyle = f.body;
      g.beginPath(); g.moveTo(10, 40); g.quadraticCurveTo(30, 110, -10, 120); g.quadraticCurveTo(-36, 112, -18, 92); g.stroke();
      g.strokeStyle = INK; g.lineWidth = 5;
      fin(g, [[24, -10], [56, 6], [28, 34]], f);
      const p = new Path2D();
      p.moveTo(-20, -96); p.quadraticCurveTo(10, -120, 26, -86); p.quadraticCurveTo(40, -40, 22, 0);
      p.quadraticCurveTo(10, 50, -8, 48); p.quadraticCurveTo(-30, 20, -10, -40); p.quadraticCurveTo(-28, -60, -66, -70);
      p.lineTo(-66, -82); p.quadraticCurveTo(-40, -84, -20, -96); p.closePath();
      paintBody(g, p, f, [-66, -120, 40, 50]);
      eye(g, -6, -86, 10);
      break;
    }
    case 'jelly': {
      g.strokeStyle = f.fin; g.lineWidth = 6;
      for (let i = 0; i < 6; i++) {
        const x = -48 + i * 19;
        g.beginPath(); g.moveTo(x, 10);
        for (let k = 1; k <= 7; k++) g.lineTo(x + Math.sin(k * 0.9 + i) * 8, 10 + k * 18);
        g.stroke();
      }
      g.strokeStyle = INK; g.lineWidth = 5;
      const p = new Path2D(); p.moveTo(-70, 14); p.bezierCurveTo(-74, -80, 74, -80, 70, 14); p.quadraticCurveTo(0, 30, -70, 14); p.closePath();
      g.globalAlpha = 0.9;
      paintBody(g, p, f, [-70, -70, 70, 30]);
      g.globalAlpha = 1;
      g.fillStyle = 'rgba(255,255,255,0.55)'; g.beginPath(); g.ellipse(-26, -34, 14, 8, -0.5, 0, Math.PI * 2); g.fill();
      break;
    }
    case 'crab': {
      g.lineWidth = 8; g.strokeStyle = INK;
      for (const s of [-1, 1]) for (let i = 0; i < 3; i++) {
        g.beginPath(); g.moveTo(s * 40, 10 + i * 8); g.lineTo(s * (80 + i * 6), 30 + i * 6); g.lineTo(s * (92 + i * 8), 64); g.stroke();
      }
      for (const s of [-1, 1]) {
        g.beginPath(); g.moveTo(s * 50, -10); g.lineTo(s * 84, -50); g.stroke();
        const c = new Path2D(); c.ellipse(s * 92, -66, 26, 18, s * 0.5, 0, Math.PI * 2);
        g.fillStyle = f.fin; g.fill(c); g.lineWidth = 5; g.stroke(c);
        g.beginPath(); g.moveTo(s * 92, -66); g.lineTo(s * 116, -76); g.stroke();
        g.lineWidth = 8;
      }
      g.lineWidth = 5;
      const p = new Path2D(); p.ellipse(0, 0, 70, 46, 0, Math.PI, 0); p.quadraticCurveTo(0, 40, -70, 0); p.closePath();
      paintBody(g, p, f, [-70, -46, 70, 30]);
      for (const s of [-1, 1]) { g.beginPath(); g.moveTo(s * 18, -40); g.lineTo(s * 22, -66); g.stroke(); eye(g, s * 22, -72, 9); }
      break;
    }
    case 'star': {
      const p = new Path2D();
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5, r = i % 2 ? 36 : 100;
        const x = Math.cos(a) * r, y = Math.sin(a) * r;
        if (i === 0) p.moveTo(x, y); else p.lineTo(x, y);
      }
      p.closePath();
      paintBody(g, p, f, [-100, -100, 100, 100]);
      g.fillStyle = f.mark;
      for (let i = 0; i < 5; i++) { const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5; for (const d of [30, 55, 78]) { g.beginPath(); g.arc(Math.cos(a) * d, Math.sin(a) * d, 5, 0, Math.PI * 2); g.fill(); } }
      break;
    }
    case 'squid': {
      g.strokeStyle = INK; g.lineWidth = 12;
      for (let i = 0; i < 6; i++) { const x = -36 + i * 14; g.beginPath(); g.moveTo(x, 30); g.quadraticCurveTo(x + Math.sin(i) * 20, 80, x + (i - 2.5) * 6, 120); g.stroke(); }
      g.strokeStyle = f.fin; g.lineWidth = 6;
      for (let i = 0; i < 6; i++) { const x = -36 + i * 14; g.beginPath(); g.moveTo(x, 30); g.quadraticCurveTo(x + Math.sin(i) * 20, 80, x + (i - 2.5) * 6, 120); g.stroke(); }
      g.strokeStyle = INK; g.lineWidth = 5;
      fin(g, [[-44, -100], [-90, -120], [-30, -70]], f);
      fin(g, [[44, -100], [90, -120], [30, -70]], f);
      const p = new Path2D(); p.moveTo(0, -130); p.quadraticCurveTo(56, -70, 46, 30); p.lineTo(-46, 30); p.quadraticCurveTo(-56, -70, 0, -130); p.closePath();
      paintBody(g, p, f, [-56, -130, 56, 30]);
      eye(g, -22, 6, 11); eye(g, 22, 6, 11);
      break;
    }
    case 'lantern': {
      // A small deep-sea fish with a glowing lure and glowing spots.
      g.beginPath(); g.moveTo(-30, -50); g.quadraticCurveTo(-60, -110, -100, -90); g.stroke();
      g.fillStyle = f.glow ?? '#c8fff6'; g.beginPath(); g.arc(-100, -90, 13, 0, Math.PI * 2); g.fill(); g.stroke();
      tailFork(g, 66, 0, 36, 30, f);
      const p = new Path2D(); p.ellipse(0, 0, 76, 58, 0, 0, Math.PI * 2);
      paintBody(g, p, f, [-76, -58, 76, 58]);
      g.fillStyle = f.glow ?? '#c8fff6';
      for (let i = 0; i < 6; i++) { g.beginPath(); g.arc(-30 + i * 16, 28 + Math.sin(i) * 4, 4.5, 0, Math.PI * 2); g.fill(); }
      g.fillStyle = INK; g.beginPath(); g.moveTo(-74, 6); g.quadraticCurveTo(-40, 22, -70, 32); g.closePath(); g.fill();
      g.fillStyle = '#ffffff';
      for (let i = 0; i < 3; i++) { g.beginPath(); g.moveTo(-68 + i * 7, 10); g.lineTo(-64 + i * 7, 20); g.lineTo(-60 + i * 7, 10); g.fill(); }
      eye(g, -40, -20, 14);
      break;
    }
    case 'dragon': {
      // A leafy sea dragon: a long body trailing leafy fins.
      for (const [x, y, s] of [[-50, -50, -1], [0, -58, -1], [52, -50, -1], [-30, 48, 1], [26, 54, 1], [80, 40, 1]] as [number, number, number][]) {
        fin(g, [[x, y * 0.6], [x + 22, y + s * 52], [x + 40, y * 0.6]], f);
      }
      g.strokeStyle = INK; g.lineWidth = 16;
      g.beginPath(); g.moveTo(80, 10); g.quadraticCurveTo(140, 40, 150, 90); g.stroke();
      g.strokeStyle = f.body; g.lineWidth = 9;
      g.beginPath(); g.moveTo(80, 10); g.quadraticCurveTo(140, 40, 150, 90); g.stroke();
      g.strokeStyle = INK; g.lineWidth = 5;
      const p = new Path2D();
      p.moveTo(-150, -12); p.lineTo(-96, -18); p.quadraticCurveTo(-50, -62, 40, -46); p.quadraticCurveTo(104, -26, 98, 10);
      p.quadraticCurveTo(40, 50, -40, 38); p.quadraticCurveTo(-84, 16, -150, 0); p.closePath();
      paintBody(g, p, f, [-150, -60, 104, 46]);
      eye(g, -78, -18, 11);
      break;
    }
    case 'otter': {
      // A sea otter swimming, facing left: round head with a pale muzzle, a long furry body, a thick tail.
      // Tail.
      const tail = new Path2D();
      tail.moveTo(70, -8); tail.quadraticCurveTo(140, -10, 150, 10); tail.quadraticCurveTo(130, 24, 70, 18); tail.closePath();
      g.fillStyle = f.fin; g.fill(tail); g.stroke(tail);
      // Back paws.
      for (const [x, y] of [[40, 30], [62, 26]]) { g.fillStyle = f.fin; g.beginPath(); g.ellipse(x, y, 13, 9, 0.4, 0, Math.PI * 2); g.fill(); g.stroke(); }
      // Body.
      const p = new Path2D();
      p.moveTo(-70, -26); p.quadraticCurveTo(0, -46, 80, -12); p.quadraticCurveTo(92, 6, 74, 22); p.quadraticCurveTo(0, 44, -70, 28); p.closePath();
      paintBody(g, p, f, [-70, -46, 92, 44]);
      // Head and ears.
      for (const [x, y] of [[-96, -46], [-58, -48]]) { g.fillStyle = f.body; g.beginPath(); g.arc(x, y, 11, 0, Math.PI * 2); g.fill(); g.stroke(); }
      const head = new Path2D(); head.arc(-80, -8, 44, 0, Math.PI * 2);
      g.fillStyle = f.body; g.fill(head); g.stroke(head);
      // Pale face and muzzle.
      g.fillStyle = f.belly; g.beginPath(); g.ellipse(-92, 4, 30, 24, 0, 0, Math.PI * 2); g.fill();
      g.beginPath(); g.ellipse(-104, 10, 18, 13, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      // Nose, mouth, whiskers.
      g.fillStyle = INK; g.beginPath(); g.ellipse(-112, 2, 7, 5, 0, 0, Math.PI * 2); g.fill();
      g.beginPath(); g.moveTo(-112, 8); g.quadraticCurveTo(-106, 16, -98, 12); g.stroke();
      g.lineWidth = 2.5;
      for (const k of [-1, 0, 1]) { g.beginPath(); g.moveTo(-100, 8 + k * 4); g.lineTo(-74, 2 + k * 9); g.stroke(); }
      g.lineWidth = 5;
      eye(g, -96, -18, 9); eye(g, -70, -20, 9);
      // Front paws, holding a shell.
      g.fillStyle = '#ffd6e5'; g.beginPath(); g.ellipse(-46, 32, 15, 11, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      for (const [x, y] of [[-58, 30], [-34, 34]]) { g.fillStyle = f.fin; g.beginPath(); g.ellipse(x, y, 10, 8, 0, 0, Math.PI * 2); g.fill(); g.stroke(); }
      break;
    }
  }
  if (f.shiny) for (const [x, y, r] of [[-70, -70, 12], [80, -50, 9], [40, 70, 10], [-100, 40, 7]]) sparkle(g, x, y, r);
}

/** The drawing, cropped to the fish. */
export function renderFish(f: FishLook): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = S; c.height = S;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.translate(S / 2, S / 2);
  g.scale(0.85, 0.85);
  drawFish(g, f);
  // Crop to what was drawn.
  const { data } = g.getImageData(0, 0, S, S);
  let x0 = S, y0 = S, x1 = -1, y1 = -1;
  for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
    if (data[(y * S + x) * 4 + 3] > 16) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  if (x1 < 0) return c;
  const out = document.createElement('canvas');
  out.width = x1 - x0 + 1; out.height = y1 - y0 + 1;
  out.getContext('2d')!.drawImage(c, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}
